import React, { useEffect } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { ScreenshotContext, useScreenshotProviderValues } from "@utils";

import { Delete } from "@ui/elements";

import { ModalsProvider } from "@components";

import { AdminRoute } from "./components/AdminRoute/AdminRoute";
import ProtectedRoute, {
  AuthProvider,
} from "./components/AuthProvider/AuthProvider";
import { LoginLayout } from "./components/LoginLayout/LoginLayout";
import { PageTemplate } from "./components/PageTemplate/PageTemplate";
import { TranslationProvider } from "./components/TranslationProvider";
import { Admin } from "./pages/admin/Admin";
import { AIPerformance } from "./pages/app/ai-performance/AIPerformance";
import { Dashboard } from "./pages/app/dashboard/Dashboard";
import { MyBets } from "./pages/app/my-bets/MyBets";
import { UpcomingTips } from "./pages/app/upcoming-tips/UpcomingTips";
import { CancelSubscription } from "./pages/cancel-subscription/cancel-subscription";
import { ConfirmEmail } from "./pages/confirm-email/confirm-email";
import { Cookies } from "./pages/cookies/cookies";
import { CreateNewPassword } from "./pages/create-new-password/create-new-password";
import { Disclaimer } from "./pages/disclaimer/disclaimer";
import { ForgotPassword } from "./pages/forgot-password/forgot-password";
import { Login } from "./pages/login/login";
import { PrivacyPolicy } from "./pages/privacy-policy/privacy-policy";
import { SignUp } from "./pages/register/sign-up";
import { DataDeletion } from "./pages/static/data-deletion";
import { SuccessSubscription } from "./pages/success-subscription/success-subscription";
import { TermsAndConditions } from "./pages/terms-and-conditions/terms-and-conditions";
import { UpdateEmailPreferences } from "./pages/update-email-preferences/update-email-preferences";
import { VerifyAccount } from "./pages/verify-account/verify-account";
import { getCookie } from "./utils/cookie";
import { getUtmParams } from "./utils/getUtmParams";

const queryClient = new QueryClient();
const reCaptchaKey = process.env.REACT_APP_RECAPTCHA_KEY || "NO_CAPTCHA_KEY";

function App() {
  const screenshotContextValue = useScreenshotProviderValues();
  useEffect(() => {
    const referrer = getCookie("user_source") || document.referrer;

    if (
      (referrer && !localStorage.getItem("user_source")) ||
      localStorage.getItem("user_source") !== referrer
    ) {
      localStorage.setItem("user_source", referrer);
    }
  }, []);

  useEffect(() => {
    const params = getUtmParams();

    localStorage.setItem("utm_source", params.utm_source);
    localStorage.setItem("utm_medium", params.utm_medium);
    localStorage.setItem("utm_campaign", params.utm_campaign);
  }, []);

  return (
    <GoogleReCaptchaProvider reCaptchaKey={reCaptchaKey}>
      <QueryClientProvider client={queryClient}>
        <TranslationProvider>
          <AuthProvider>
            <ModalsProvider>
              <ScreenshotContext.Provider value={screenshotContextValue}>
                <ToastContainer
                  pauseOnFocusLoss={false}
                  closeButton={({ closeToast }) => (
                    <Delete onClick={closeToast} />
                  )}
                />
                <Router>
                  <Routes>
                    <Route element={<PageTemplate />}>
                      <Route element={<ProtectedRoute />}>
                        <Route element={<AdminRoute />}>
                          <Route path="/admin" element={<Admin />} />
                        </Route>
                        <Route path="/" element={<Dashboard />} />
                        <Route
                          path="/upcoming-tips"
                          element={<UpcomingTips />}
                        />
                        <Route
                          path="/ai-performance"
                          element={<AIPerformance />}
                        />
                        <Route path="/my-bets" element={<MyBets />} />
                        <Route
                          path="/success-subscription"
                          element={<SuccessSubscription />}
                        />
                        <Route
                          path="/cancel-subscription"
                          element={<CancelSubscription />}
                        />
                      </Route>
                    </Route>
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/disclaimer" element={<Disclaimer />} />
                    <Route
                      path="/terms-and-conditions"
                      element={<TermsAndConditions />}
                    />
                    <Route path="/cookies" element={<Cookies />} />
                    <Route path="/data-deletion" element={<DataDeletion />} />
                    <Route element={<LoginLayout />}>
                      <Route path="/login" element={<Login />} />
                      <Route
                        path="/forgot-password"
                        element={<ForgotPassword />}
                      />
                      <Route
                        path="/create-new-password"
                        element={<CreateNewPassword />}
                      />
                      <Route path="/confirm-email" element={<ConfirmEmail />} />
                      <Route
                        path="/verify-account"
                        element={<VerifyAccount />}
                      />
                      <Route path="/sign-up" element={<SignUp />} />
                      <Route
                        path="/update-email-preferences"
                        element={<UpdateEmailPreferences />}
                      />
                    </Route>
                  </Routes>
                </Router>
              </ScreenshotContext.Provider>
            </ModalsProvider>
          </AuthProvider>
        </TranslationProvider>
      </QueryClientProvider>
    </GoogleReCaptchaProvider>
  );
}

export default App;
